/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Open Source Version is distributed with Sencha Ext JS pursuant to a FLOSS Exception agreed upon
 * between Sonatype, Inc. and Sencha Inc. Sencha Ext JS is licensed under GPL v3 and cannot be redistributed as part of a
 * closed source work.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
/**
 * Preview Admin Routes - Phase 4.0 Fork Infrastructure
 * 
 * Admin (Settings) routes for Preview UI.
 * Uses SettingsPageLayoutRadix for secondary sidebar navigation.
 * 
 * IMPORTANT: Route names must match the default admin route names (with preview. prefix)
 * so that useDefaultAdminRouteName() works correctly.
 * 
 * URL Structure (path-based routing):
 * - /preview/admin/repositories → preview.admin.repository.repositories
 * - /preview/admin/blobstores   → preview.admin.repository.blobstores
 * - etc.
 */

import React from 'react';
import {UIView} from '@uirouter/react';
import {
  Permissions,
  SettingsPageLayoutRadix,
  SettingsHubPage,
  SonatypeTestHub,
  SonatypeInternalTestDashboard,
  SonatypeInternalTestMalwareDefense,
  SonatypeInternalTestSearchFw,
  SonatypeInternalTestSearchHc,
  MalwareDashboardScenarioPage,
  RepositoriesPage,
  ContentSelectorsPage,
  CleanupPoliciesPage,
  RoutingRulesPage,
  ProprietaryPage,
  GlobalDistributionPage,
  PrivilegesPage,
  RolesPage,
  UsersPage,
  ServiceAccountTokensPage,
  UserTokensPage,
  MetricHealthPage,
  TasksPage,
  CapabilitiesPage,
  PreviewUiSettingsPage,
  EmailPage,
  HttpPage,
  UsagePage,
  IqServerPage,
  HostedRepoEvaluationSetupPage,
  IpAllowList,
  PRO_FAMILY_EDITIONS,
  EDITION_ENTERPRISE,
} from '@sonatype/nexus-ui-plugin';

// Sonatype-internal scenario route wraps MalwareDashboardScenarioPage with
// a transition-param unwrapper. The named export from ui-plugin is the
// underlying (non-default) export from MalwareDashboardTestPage.
const SonatypeInternalTestScenario = ({ transition }) => {
  const id = transition?.params?.().scenarioId ?? 'A';
  return React.createElement(MalwareDashboardScenarioPage, { scenarioId: id });
};

const sonatypeInternalRoutes = [
  // Hub — index of all test pages
  {
    name: 'preview.test',
    url: '/test',
    component: SonatypeTestHub,
    data: { title: '[Internal] Test Hub' },
  },
  // Dashboard / MalwareStatusCard
  {
    name: 'preview.test-dashboard',
    url: '/test-dashboard',
    component: SonatypeInternalTestDashboard,
    data: { title: '[Internal] Test — Dashboard' },
  },
  {
    name: 'preview.test-dashboard.scenario',
    url: '/test-dashboard/scenario/:scenarioId',
    component: SonatypeInternalTestScenario,
    data: { title: '[Internal] Dashboard Scenario' },
  },
  // Malware Defense page states
  {
    name: 'preview.test-malware-defense',
    url: '/test-malware-defense',
    component: SonatypeInternalTestMalwareDefense,
    data: { title: '[Internal] Test — Malware Defense' },
  },
  // Firewall cell permutations
  {
    name: 'preview.test-search-fw',
    url: '/test-search-fw',
    component: SonatypeInternalTestSearchFw,
    data: { title: '[Internal] Test — Firewall Cell' },
  },
  // Health Check cell permutations
  {
    name: 'preview.test-search-hc',
    url: '/test-search-hc',
    component: SonatypeInternalTestSearchHc,
    data: { title: '[Internal] Test — Health Check Cell' },
  },
];

// =============================================================================
// ROUTE DEFINITIONS
// Route names MUST match default admin routes (preview.admin.repository.repositories, etc.)
// URLs are flat at the admin level
// =============================================================================

// Exported separately so test pages can be registered at the preview.* level
// (not preview.admin.*) — they render standalone without the Settings sidebar.
export const sonatypeInternalTestRoutes = sonatypeInternalRoutes;

export const previewAdminRoutes = [
  // =============================================================================
  // SETTINGS HUB - Standalone at preview.settings (no sidebar)
  // =============================================================================
  {
    name: 'preview.settings',
    url: '/settings',
    component: SettingsHubPage,
    data: {
      title: 'Settings',
      visibilityRequirements: {
        requiresAnyPermission: [
          Permissions.REPOSITORY_ADMIN.READ,
          Permissions.BLOB_STORES.READ,
          Permissions.SELECTORS.READ,
          Permissions.PRIVILEGES.READ,
          Permissions.ROLES.READ,
          Permissions.USERS.READ,
          Permissions.SETTINGS.READ,
          Permissions.SSL_TRUSTSTORE.READ,
          Permissions.LDAP.READ,
          Permissions.USER_TOKENS_SETTINGS.READ,
          'nexus:crowd:read',
          Permissions.TASKS.READ,
          Permissions.CAPABILITIES.READ,
          Permissions.LOGGING.READ,
          Permissions.ATLAS.READ,
          Permissions.METRICS.READ,
          Permissions.LICENSING.READ,
        ],
      },
    },
  },
  // =============================================================================
  // ROOT ADMIN ROUTE - Abstract parent for /admin paths
  // =============================================================================
  {
    name: 'preview.admin',
    url: '/admin',
    abstract: true,
    component: UIView,
  },

  // =============================================================================
  // SETTINGS HUB REDIRECT - /admin/settings redirects to Settings Hub
  // =============================================================================
  {
    name: 'preview.admin.settings',
    url: '/settings',
    redirectTo: 'preview.settings',
  },

  // =============================================================================
  // ADMIN PAGES WRAPPER - Provides sidebar for all settings pages
  // =============================================================================
  {
    name: 'preview.admin.pages',
    url: '',
    component: SettingsPageLayoutRadix,
    data: {
      visibilityRequirements: {
        requiresAnyPermission: [
          Permissions.REPOSITORY_ADMIN.READ,
          Permissions.BLOB_STORES.READ,
          Permissions.SELECTORS.READ,
          Permissions.PRIVILEGES.READ,
          Permissions.ROLES.READ,
          Permissions.USERS.READ,
          Permissions.SETTINGS.READ,
          Permissions.SSL_TRUSTSTORE.READ,
          Permissions.LDAP.READ,
          Permissions.USER_TOKENS_SETTINGS.READ,
          'nexus:crowd:read',
          Permissions.TASKS.READ,
          Permissions.CAPABILITIES.READ,
          Permissions.LOGGING.READ,
          Permissions.ATLAS.READ,
          Permissions.METRICS.READ,
          Permissions.LICENSING.READ,
        ],
      },
    },
  },

  // =============================================================================
  // REPOSITORY SECTION
  // Names match: admin.repository.* → preview.admin.repository.*
  // =============================================================================
  {
    name: 'preview.admin.repository',
    url: '/repository',
    redirectTo: 'preview.admin.repository.repositories.list', /* DO NOT CHANGE - blobstores not on cloud (Agent0 fix) */
    component: UIView,
    data: { title: 'Repository' },
  },
  {
    name: 'preview.admin.repository.repositories',
    url: '/repositories',
    abstract: true,
    component: UIView,
    data: {
      title: 'Repositories',
      // NEXUS-54048: mirror Classic/Default UI (adminRoutes.js REPOSITORIES.ROOT) repository-admin prefix.
      visibilityRequirements: {
        permissionPrefix: 'nexus:repository-admin',
      },
    },
  },
  {
    name: 'preview.admin.repository.repositories.list',
    url: '',
    component: RepositoriesPage,
    data: { title: 'Repositories' },
  },
  {
    name: 'preview.admin.repository.repositories.create',
    url: '/create',
    component: RepositoriesPage,
    data: { title: 'Create Repository' },
  },
  {
    name: 'preview.admin.repository.repositories.createForm',
    url: '/create/:format/:type',
    component: RepositoriesPage,
    data: { title: 'Create Repository' },
  },
  {
    name: 'preview.admin.repository.repositories.detail',
    url: '/:repositoryName?tab',
    component: RepositoriesPage,
    params: {
      tab: { value: null, type: 'string', dynamic: true },
    },
    data: { title: 'Repository Details' },
  },
  {
    name: 'preview.admin.repository.selectors',
    url: '/selectors',
    abstract: true,
    component: UIView,
    data: {
      title: 'Content Selectors',
      // NEXUS-54048: mirror Classic/Default UI (adminRoutes.js SELECTORS.ROOT).
      visibilityRequirements: {
        requiresPermission: Permissions.SELECTORS.READ,
      },
    },
  },
  {
    name: 'preview.admin.repository.selectors.list',
    url: '',
    component: ContentSelectorsPage,
    data: { title: 'Content Selectors' },
  },
  {
    name: 'preview.admin.repository.selectors.create',
    url: '/create',
    component: ContentSelectorsPage,
    data: { title: 'Create Content Selector' },
  },
  {
    name: 'preview.admin.repository.selectors.detail',
    url: '/:name',
    component: ContentSelectorsPage,
    data: { title: 'Content Selector Details' },
  },
  {
    name: 'preview.admin.repository.cleanuppolicies',
    url: '/cleanup-policies',
    abstract: true,
    component: UIView,
    data: {
      title: 'Cleanup Policies',
      // NEXUS-54048: mirror Classic/Default UI (adminRoutes.js CLEANUPPOLICIES.ROOT) — nexus:*.
      visibilityRequirements: {
        requiresPermission: Permissions.ADMIN,
      },
    },
  },
  {
    name: 'preview.admin.repository.cleanuppolicies.list',
    url: '',
    component: CleanupPoliciesPage,
    data: { title: 'Cleanup Policies' },
  },
  {
    name: 'preview.admin.repository.cleanuppolicies.create',
    url: '/create',
    component: CleanupPoliciesPage,
    data: { title: 'Create Cleanup Policy' },
  },
  {
    name: 'preview.admin.repository.cleanuppolicies.detail',
    url: '/:name',
    component: CleanupPoliciesPage,
    data: { title: 'Cleanup Policy Details' },
  },
  {
    name: 'preview.admin.repository.routingrules',
    url: '/routing-rules',
    abstract: true,
    redirectTo: 'preview.admin.repository.routingrules.list',
    component: UIView,
    data: {
      title: 'Routing Rules',
      // NEXUS-54048: mirror Classic/Default UI (adminRoutes.js ROUTINGRULES.ROOT). RoutingRulesResource
      // requires nexus:* on every endpoint including the GET/list, so settings:read would 403 on load.
      visibilityRequirements: {
        requiresPermission: Permissions.ADMIN,
      },
    },
  },
  {
    name: 'preview.admin.repository.routingrules.list',
    url: '',
    component: RoutingRulesPage,
    data: { title: 'Routing Rules' },
  },
  {
    name: 'preview.admin.repository.routingrules.create',
    url: '/create',
    component: RoutingRulesPage,
    data: { title: 'Create Routing Rule' },
  },
  {
    name: 'preview.admin.repository.routingrules.preview',
    url: '/preview',
    component: RoutingRulesPage,
    data: { title: 'Routing Rules Preview' },
  },
  {
    name: 'preview.admin.repository.routingrules.detail',
    url: '/:name',
    component: RoutingRulesPage,
    data: { title: 'Routing Rule Details' },
  },
    {
    name: 'preview.admin.repository.proprietary',
    url: '/proprietary',
    component: ProprietaryPage,
    data: {
      title: 'Proprietary Repositories',
      // NEXUS-54048: mirror Classic/Default UI (adminRoutes.js PROPRIETARY.ROOT).
      visibilityRequirements: {
        requiresPermission: Permissions.SETTINGS.READ,
      },
    },
  },
  {
    name: 'preview.admin.repository.globaldistribution',
    url: '/global-distribution',
    component: GlobalDistributionPage,
    data: {
      title: 'Global Distribution',
      visibilityRequirements: {
        requiresPermission: Permissions.SETTINGS.READ,
        editions: [EDITION_ENTERPRISE],
      },
    },
  },

  // =============================================================================
  // SECURITY SECTION  
  // Names match: admin.security.* → preview.admin.security.*
  // =============================================================================
  {
    name: 'preview.admin.security',
    url: '/security',
    redirectTo: 'preview.admin.security.privileges.list',
    component: UIView,
    data: { title: 'Security' },
  },
  {
    name: 'preview.admin.security.privileges',
    url: '/privileges',
    abstract: true,
    component: UIView,
    data: {
      title: 'Privileges',
      // NEXUS-54048: gate on the abstract parent so the ancestor walk protects every child
      // (.list/.create/.createForm/.profile/.detail), matching coreui. A gate on .list only
      // left the sibling create/detail routes reachable by unauthorized users.
      visibilityRequirements: {
        requiresPermission: 'nexus:privileges:read',
      },
    },
  },
  {
    name: 'preview.admin.security.privileges.list',
    url: '',
    component: PrivilegesPage,
    data: {
      title: 'Privileges',
    },
  },
  {
    name: 'preview.admin.security.privileges.create',
    url: '/create',
    component: PrivilegesPage,
    data: { title: 'Create Privilege' },
  },
  {
    name: 'preview.admin.security.privileges.createForm',
    url: '/create/:typeId',
    component: PrivilegesPage,
    params: { typeId: { type: 'string', value: null, dynamic: true } },
    data: { title: 'Create Privilege' },
  },
  {
    name: 'preview.admin.security.privileges.profile',
    url: '/:privilegeId/profile',
    component: PrivilegesPage,
    data: { title: 'Privilege Profile' },
  },
  {
    name: 'preview.admin.security.privileges.detail',
    url: '/:privilegeId',
    component: PrivilegesPage,
    data: { title: 'Privilege Details' },
  },
  {
    name: 'preview.admin.security.roles',
    url: '/roles',
    abstract: true,
    component: UIView,
    data: {
      title: 'Roles',
      // NEXUS-54048: gate on the abstract parent so the ancestor walk protects every child
      // (.list/.create/.detail/.profile), matching coreui. A gate on .list only left the
      // sibling create/detail routes reachable by unauthorized users. Mirrors Classic/Default
      // UI (adminRoutes.js ROLES.ROOT) — requires BOTH roles:read and privileges:read
      // (permissions[] is AND).
      visibilityRequirements: {
        permissions: [Permissions.ROLES.READ, Permissions.PRIVILEGES.READ],
      },
    },
  },
  {
    name: 'preview.admin.security.roles.list',
    url: '',
    component: RolesPage,
    data: {
      title: 'Roles',
    },
  },
  {
    name: 'preview.admin.security.roles.create',
    url: '/create',
    component: RolesPage,
    data: { title: 'Create Role' },
  },
  {
    name: 'preview.admin.security.roles.detail',
    url: '/:roleId',
    component: RolesPage,
    data: { title: 'Role Details' },
  },
  {
    name: 'preview.admin.security.roles.profile',
    url: '/:roleId/profile',
    component: RolesPage,
    data: { title: 'Role Profile' },
  },
  {
    name: 'preview.admin.security.users',
    url: '/users',
    abstract: true,
    redirectTo: 'preview.admin.security.users.list',
    component: UIView,
    data: {
      title: 'Users',
      // NEXUS-54048: mirror Classic/Default UI (adminRoutes.js USERS.ROOT) — requires BOTH
      // users:read and roles:read (permissions[] is AND).
      visibilityRequirements: {
        permissions: [Permissions.USERS.READ, Permissions.ROLES.READ],
      },
    },
  },
  {
    name: 'preview.admin.security.users.list',
    url: '',
    component: UsersPage,
    data: { title: 'Users' },
  },
  {
    name: 'preview.admin.security.users.create',
    url: '/create',
    component: UsersPage,
    data: { title: 'Create User' },
  },
  {
    name: 'preview.admin.security.users.invite',
    url: '/invite',
    component: UsersPage,
    data: { title: 'Invite User' },
  },
  {
    name: 'preview.admin.security.users.detail',
    url: '/:userId/:source',
    component: UsersPage,
    params: {
      userId: { type: 'string', value: null, dynamic: true },
      source: { type: 'string', value: null, dynamic: true },
    },
    data: { title: 'User Details' },
  },
  {
    name: 'preview.admin.security.users.profile',
    url: '/:userId/:source/profile',
    component: UsersPage,
    params: {
      userId: { type: 'string', value: null, dynamic: true },
      source: { type: 'string', value: null, dynamic: true },
    },
    data: { title: 'User Profile' },
  },
  {
    name: 'preview.admin.security.usertoken',
    url: '/user-tokens',
    component: UserTokensPage,
    data: {
      title: 'User Tokens',
      // NEXUS-54048: mirror Classic/Default UI (adminRoutes.js USERTOKEN.ROOT) — Cloud is
      // gated here; the self-hosted Pro-family gate lives in the public preview routes.
      visibilityRequirements: {
        requiresPermission: Permissions.USER_TOKENS_SETTINGS.READ,
        editions: PRO_FAMILY_EDITIONS,
      },
    },
  },
  {
    name: 'preview.admin.security.serviceaccounttokens',
    url: '/service-account-tokens',
    component: ServiceAccountTokensPage,
    data: {
      title: 'Service Account Tokens',
      visibilityRequirements: {
        requiresPermission: Permissions.SERVICE_ACCOUNTS.READ,
        editions: PRO_FAMILY_EDITIONS,
      },
    },
  },
  {
    name: 'preview.admin.security.ip-allowlist',
    url: '/ip-allowlist',
    component: IpAllowList,
    data: {
      title: 'IP Allow List',
      // NEXUS-54048: gate on nexus:* to match the backend. IpAllowListResource requires
      // @RequiresPermissions("nexus:*") on every endpoint (admin-only by design, NEXUS-45598).
      // A settings:read gate let non-admins open the page and then hit a 403.
      visibilityRequirements: {
        requiresPermission: Permissions.ADMIN,
        editions: PRO_FAMILY_EDITIONS,
      },
    },
  },

  // =============================================================================
  // SUPPORT SECTION
  // Names match: admin.support.* → preview.admin.support.*
  // =============================================================================
  {
    name: 'preview.admin.support',
    url: '/support',
    redirectTo: 'preview.admin.support.metrichealth',
    component: UIView,
    data: { title: 'Support' },
  },
  {
    name: 'preview.admin.support.metrichealth',
    url: '/metrichealth',
    component: MetricHealthPage,
    data: {
      title: 'Metric Health',
      visibilityRequirements: {
        requiresPermission: 'nexus:metrics:read',
      },
    },
  },

  // =============================================================================
  // SYSTEM SECTION
  // Names match: admin.system.* → preview.admin.system.*
  // =============================================================================
  {
    name: 'preview.admin.system',
    url: '/system',
    redirectTo: 'preview.admin.system.tasks.list',
    component: UIView,
    data: { title: 'System' },
  },
  {
    name: 'preview.admin.system.tasks',
    url: '/tasks',
    abstract: true,
    component: UIView,
    data: {
      title: 'Tasks',
      // NEXUS-54048: mirror Classic/Default UI (adminRoutes.js TASKS.ROOT).
      visibilityRequirements: {
        requiresPermission: Permissions.TASKS.READ,
      },
    },
  },
  {
    name: 'preview.admin.system.tasks.list',
    url: '',
    component: TasksPage,
    data: { title: 'Tasks' },
  },
  {
    name: 'preview.admin.system.tasks.create',
    url: '/create',
    component: TasksPage,
    data: { title: 'Create Task' },
  },
  {
    name: 'preview.admin.system.tasks.createWithType',
    url: '/create/:typeId',
    component: TasksPage,
    data: { title: 'Create Task' },
  },
  {
    name: 'preview.admin.system.tasks.detail',
    url: '/:taskId',
    component: TasksPage,
    data: { title: 'Task Details' },
  },
  {
    name: 'preview.admin.system.capabilities',
    url: '/capabilities',
    abstract: true,
    component: UIView,
    data: {
      title: 'Capabilities',
      // NEXUS-54048: mirror Classic/Default UI (adminRoutes.js CAPABILITIES.ROOT).
      visibilityRequirements: {
        requiresPermission: Permissions.CAPABILITIES.READ,
      },
    },
  },
  {
    name: 'preview.admin.system.capabilities.list',
    url: '',
    component: CapabilitiesPage,
    data: { title: 'Capabilities' },
  },
  {
    name: 'preview.admin.system.capabilities.create',
    url: '/create',
    component: CapabilitiesPage,
    data: { title: 'Create Capability' },
  },
  {
    name: 'preview.admin.system.capabilities.createWithType',
    url: '/create/:typeId',
    component: CapabilitiesPage,
    params: { typeId: { type: 'string', value: null, dynamic: true } },
    data: { title: 'Create Capability' },
  },
  {
    name: 'preview.admin.system.capabilities.detail',
    url: '/:capabilityId',
    component: CapabilitiesPage,
    params: { capabilityId: { type: 'string', value: null, dynamic: true } },
    data: { title: 'Capability Details' },
  },
  {
    name: 'preview.admin.system.previewui',
    url: '/previewui',
    component: PreviewUiSettingsPage,
    data: { 
      title: 'Nexus One UI',
      visibilityRequirements: {
        permissions: [Permissions.SETTINGS.READ],
      },
    },
  },
  {
    name: 'preview.admin.system.emailserver',
    url: '/emailserver',
    component: EmailPage,
    data: { title: 'Email Server' },
  },
  {
    name: 'preview.admin.system.http',
    url: '/http',
    component: HttpPage,
    data: { title: 'HTTP' },
  },
        {
    name: 'preview.admin.system.usage',
    url: '/usage',
    component: UsagePage,
    data: {
      title: 'Usage',
      // NEXUS-54048: mirror cloud Classic (cloud adminRoutes.js HISTORICAL_USAGE) — settings:read.
      visibilityRequirements: {
        permissions: [Permissions.SETTINGS.READ],
      },
    },
  },

  // =============================================================================
  // IQ SERVER (top-level admin route, not nested under system)
  // Tabbed UI with Overview and Connection Settings tabs.
  // =============================================================================
  {
    name: 'preview.admin.iq',
    url: '/iq?tab',
    component: IqServerPage,
    params: {
      tab: { value: null, type: 'string', dynamic: true },
    },
    data: {
      title: 'IQ Server',
      visibilityRequirements: {
        requiresPermission: 'nexus:settings:read',
      },
    },
  },

  // Legacy routes — preserve bookmarks by redirecting into the new tabbed page.
  {
    name: 'preview.admin.iqConnected',
    url: '/iq/connected',
    redirectTo: { state: 'preview.admin.iq', params: { tab: 'overview' } },
  },
  {
    name: 'preview.admin.iqConnection',
    url: '/iq/connection',
    redirectTo: { state: 'preview.admin.iq', params: { tab: 'connection-settings' } },
  },
  {
    name: 'preview.admin.iqOverview',
    url: '/iq-overview',
    redirectTo: 'preview.admin.iq',
  },

  // Screen 3 — Hosted Repository Evaluation workflow (Repositories + Settings tabs).
  {
    name: 'preview.admin.iqHostedReposEval',
    url: '/iq/hosted-repos-eval?tab',
    component: HostedRepoEvaluationSetupPage,
    data: {
      title: 'Set up Hosted Repository Evaluation',
      visibilityRequirements: {
        requiresPermission: 'nexus:settings:read',
        statesEnabled: [
          { key: 'hostedRepositoryEvaluationEnabled', defaultValue: false },
        ],
      },
    },
  },

  // =============================================================================
  // SONATYPE INTERNAL TEST PAGES — now registered at preview.test* level
  // (standalone, outside SettingsPageLayoutRadix) via sonatypeInternalTestRoutes
  // export in routerConfig.js — removed from here (xtrz).
  //
  // Redirect stubs so bookmarked #preview/admin/test* URLs still work.
  // =============================================================================
  {
    name: 'preview.admin.test',
    url: '/test',
    redirectTo: 'preview.test',
  },
  {
    name: 'preview.admin.test-dashboard',
    url: '/test-dashboard',
    redirectTo: 'preview.test-dashboard',
  },
  {
    name: 'preview.admin.test-malware-defense',
    url: '/test-malware-defense',
    redirectTo: 'preview.test-malware-defense',
  },
  {
    name: 'preview.admin.test-search-fw',
    url: '/test-search-fw',
    redirectTo: 'preview.test-search-fw',
  },
  {
    name: 'preview.admin.test-search-hc',
    url: '/test-search-hc',
    redirectTo: 'preview.test-search-hc',
  },

  // =============================================================================
  // USER ACCOUNT — redirect to standalone preview.user.account (outside Settings layout)
  // =============================================================================
  {
    name: 'preview.admin.useraccount',
    url: '/useraccount',
    // Redirect so old/bookmarked links land on the standalone page, not Settings
    redirectTo: 'preview.user.account',
    data: { title: 'User Account' },
  },
];
